import { MutableRefObject, useContext, useEffect, useState } from 'react';
import ApplicationContext from '../../../ApplicationContext';
import useGlobalOkDialog from '../../../hooks/useGlobalOkDialog';
import { JobNames, getTbNameByJobName } from '../../../framework.core/JobNames';
import SecondaryButton from '../../../ui/SecondaryButton';
import Label from '../../../ui/Label';
import InlineInputControlRow from '../../../ui/layout/InlineInputControlRow';
import TextBox from '../../../ui/TextBox';
import { ApiResult } from '../../../framework.core/data/ApiResult';
import { InvoHd } from '../../../fms.core/InvoHd';
import { AcctHeadType, getAcctHeadTypeByTBName } from '../../../fms.enum';
import { useTypedFastStorage } from '../../../hooks/useFastStorage';
import CopyInvTargetBLLookupField from '../../../uiControls/lookup/CopyInvTargetBLLookupField';
import { openNewPage, NavigationType } from '../../../util/navigationHelpers';
import { useHistory } from 'react-router';
import useJobMenuUrlQuery from '../../../hooks/useJobMenuUrlQuery';
import CopyInvTargetAcctLookupField from '../../../uiControls/lookup/CopyInvTargetAcctLookupField';
import PrimaryButton from '../../../ui/PrimaryButton';


interface ICopyInvoice {
    [key: string]: any,
    fieldLabel: FieldLabel,
    baseInfoDetail: BaseInfoDetail
};

interface FieldLabel {
    blType: string,
    mblName: string,
    hblName: string,
};

interface BaseInfoDetail {
    refNo: string,
    mblNo: string,
    hblNo: string,
    targetId: number,
    invNo: string,
    jobId: number,
    scrJobId: number,
};

interface CopyInoviceStorageKeys {
    modelStorage: string,
};

function getCopyInoviceStorageKeys(): CopyInoviceStorageKeys {
    return {modelStorage: 'common-copyinvoice'}
}


function CopyInvoiceImpl({
  jobName,
  jobId,
  srcJobId,
  srcJobName,
  onPopupClose,
  isFromCopy,
  invoiceNo
}: {
  jobName: JobNames,
  jobId: number,
  srcJobName: JobNames,
  srcJobId: number,
  onPopupClose: () => void,
  isFromCopy? :boolean,
  invoiceNo?: string
}) {

  const {modelStorage} = getCopyInoviceStorageKeys();

  const appContext = useContext(ApplicationContext);

  const [showGlobalOkDialog] = useGlobalOkDialog();

  const [initialized, setInitialized] = useState<boolean>(false);

  const [inStorage, setInStorage, removeCopyInvoiceFromStorage, copyInvoiceRef] = useTypedFastStorage<ICopyInvoice>(modelStorage, null);

  useEffect(() => {
    initialize();
    return () => removeCopyInvoiceFromStorage()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setTimeout(() => {
        const lookupId = (isFromCopy ? 'invoice-copy-Acct' : 'invoice-copy');
        const lookup = document.getElementById(lookupId) as HTMLInputElement | null;
        if (lookup) {
            const value = lookup.value.trim();
            if (isFromCopy && value) {
                document.getElementById('copyfrom-ok-button')?.focus();
            } else {
                lookup.focus();
            }                
        }
    }, 30);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized]);

  async function readBaseInfo(){

    const hdTbName = getTbNameByJobName(srcJobName);
    const hdOpType = getAcctHeadTypeByTBName(hdTbName);
    const baseInfoDetail : BaseInfoDetail = {refNo: '', mblNo: '', hblNo: '', targetId: 0, invNo : invoiceNo ?? '', jobId: jobId ?? 0, scrJobId:0};
    const apiResult: ApiResult<InvoHd> = await appContext.api.getBLHDInfo(hdOpType, srcJobId, '');
    if(apiResult && apiResult.result && apiResult.result.blHdInfo){
        baseInfoDetail.refNo = apiResult.result.blHdInfo.mainRefNo;
        baseInfoDetail.mblNo = ((hdOpType === AcctHeadType.WHR || hdOpType === AcctHeadType.WHS) ? apiResult.result.blHdInfo.centerNo : apiResult.result.blHdInfo.mainMBLNo);
        baseInfoDetail.hblNo = apiResult.result.blHdInfo.mainHBLNo;
        baseInfoDetail.targetId = srcJobId;
        setInStorage({baseInfoDetail: baseInfoDetail});
    }
    return baseInfoDetail;
}

  async function initialize(){
    if(!inStorage){
       const fieldLabel = getPopupLabel(srcJobName);
       const baseInfoDetail =  await readBaseInfo();
       setInStorage({fieldLabel: fieldLabel, baseInfoDetail: baseInfoDetail})
    }
    setInitialized(true);
}


if (!initialized || !inStorage) {
    return null;
  }

  function getPopupLabel(srcJobName: string) {
    const fieldLabel : FieldLabel = {blType: '', mblName: '', hblName: ''} ;
    switch (srcJobName) {
        case JobNames.OIMbl:
            fieldLabel.blType = "Ocean Import Master";
            fieldLabel.mblName = "Master B/L #";
            break;
            case JobNames.OIHbl:    
            fieldLabel.blType = "Ocean Import House";
            fieldLabel.mblName = "Master B/L #";
            fieldLabel.hblName = "House B/L #";
            break;
        case JobNames.OEMbl:    
            fieldLabel.blType = "Ocean Export Master";
            fieldLabel.mblName = "Master B/L #";
            break;
        case JobNames.OEHbl:    
            fieldLabel.blType = "Ocean Export House";
            fieldLabel.mblName = "Master B/L #";
            fieldLabel.hblName = "House B/L #";
            break;
        case JobNames.AIMbl:    
            fieldLabel.blType = "Air Import Master";
            fieldLabel.mblName = "MAWB #";
            break;
        case JobNames.AIHbl:    
            fieldLabel.blType = "Air Import House";
            fieldLabel.mblName = "MAWB #";
            fieldLabel.hblName = "HAWB #";
            break;
        case JobNames.AEMbl:    
            fieldLabel.blType = "Air Export Master";
            fieldLabel.mblName = "MAWB #";
            break;
        case JobNames.AEHbl:    
            fieldLabel.blType = "Air Export House";
            fieldLabel.mblName = "MAWB #";
            fieldLabel.hblName = "HAWB #";
            break;
        case JobNames.OtherOP:  
            fieldLabel.blType = "Other Shipment";
            fieldLabel.mblName = "Master B/L #";
            fieldLabel.hblName = "House B/L #";
            break;    
        case JobNames.WHRcv:  
            fieldLabel.blType = "Warehouse Receiving";
            fieldLabel.mblName = "Customer (W/H)";
            break;    
        case JobNames.WHShip:  
            fieldLabel.blType = "Warehouse Shipping";
            fieldLabel.mblName = "Customer (W/H)";
            break;    
    }
    return fieldLabel;
  }

  return (
    <CopyInvoiceInitializedImpl
        copyInvoice={inStorage}
        copyInvoiceRef={copyInvoiceRef}
        setCopyInvoice={setInStorage}
        onPopupClose={onPopupClose}
        srcJobName={srcJobName}
        jobName={jobName}
        srcJobId={srcJobId}
        jobId={jobId}
        isFromCopy={isFromCopy}
    />
  );

}

function CopyInvoiceInitializedImpl({
    copyInvoice,
    copyInvoiceRef,
    setCopyInvoice,
    onPopupClose,
    srcJobName,
    jobName,
    srcJobId,
    jobId,
    isFromCopy
}:{
    copyInvoice: ICopyInvoice,
    copyInvoiceRef: MutableRefObject<ICopyInvoice|null>,
    setCopyInvoice: (x: Partial<ICopyInvoice>) =>void,
    onPopupClose: () => void,
    srcJobName : JobNames,
    jobName : JobNames,
    srcJobId: number,
    jobId: number,
    isFromCopy?: boolean
}){

    const hasOpenedScreenStartingWithUrl = useJobMenuUrlQuery();
    const [showGlobalOkDialog] = useGlobalOkDialog();
    const history = useHistory();

    function onChange(change: Partial<ICopyInvoice>){
        setCopyInvoice(change);
    }

    async function onCopy() {
        const job = jobName === JobNames.Invoice ? 'invoice' : 'bill';
        if (isFromCopy) {
            if (copyInvoice.baseInfoDetail.jobId === 0) {
                onPopupClose();
                return;
            }
        }
        else {
            if (copyInvoice.baseInfoDetail.targetId === 0) {
                onPopupClose();
                return;
            }
        }

        if (hasOpenedScreenStartingWithUrl(`/accounting/${job}/add/${srcJobName}`)) {
            await showGlobalOkDialog(<p>New operation is already in progress</p>);
            return;
        }
        

        const newLink = isFromCopy ? `/accounting/${job}/add/${srcJobName}/${srcJobId}?copyId=${copyInvoice.baseInfoDetail.jobId}`: 
        `/accounting/${job}/add/${srcJobName}/${copyInvoice.baseInfoDetail.targetId}?copyId=${jobId}`;




        const locationState = history.location?.state as any;
        if (locationState?.afterScreenUrl) {
            openNewPage(history, newLink, NavigationType.newPage, null, locationState?.afterScreenUrl); // From Detail
        }
        else {
            const parentOp = history.location.search?.split("parentOp=")[1];
            openNewPage(history, newLink, NavigationType.newPage, null, parentOp ? parentOp : null);    // From List
        }

    }

    return (
        <div className='report-popup'>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontWeight: 'bold' }}>
                <div style={{ paddingBottom: '10px', width: '370px'}}>
                    <InlineInputControlRow smallMargin mode="w150" label="Type: " alignRight={true}>
                        <Label label={copyInvoice.fieldLabel.blType ?? ''} style={{ fontSize: '14px' }} />
                    </InlineInputControlRow>
                </div>
                <div style={{ paddingBottom: '10px', width: '370px'}}>
                    {!isFromCopy && 
                    <InlineInputControlRow smallMargin mode="w150" label={'Target Ref. #'} alignRight={true}>
                        <CopyInvTargetBLLookupField
                            lookupId="invoice-copy"
                            initialValue={copyInvoice.baseInfoDetail.refNo ?? ''}
                            onSelect={c => {
                                const tempbaseInfo = copyInvoice.baseInfoDetail;
                                tempbaseInfo.refNo = c.refNo;
                                tempbaseInfo.mblNo = c.mblNo;
                                tempbaseInfo.hblNo = c.hblNo;
                                tempbaseInfo.targetId = c.id;
                                onChange({ baseInfoDetail: tempbaseInfo })
                            }}
                            onTextChange={c => {
                                const tempbaseInfo = copyInvoice.baseInfoDetail;
                                tempbaseInfo.refNo = c ?? '';
                                tempbaseInfo.targetId = 0;
                                onChange({ baseInfoDetail: tempbaseInfo })
                            }}
                            placeholder={undefined}
                            jobType={srcJobName}
                        />
                    </InlineInputControlRow>}
                    {isFromCopy && 
                    <InlineInputControlRow smallMargin mode="w150" label={jobName === JobNames.Invoice ? 'Target Inv #' : 'Target Bill #'} alignRight={true}>
                        <CopyInvTargetAcctLookupField
                            lookupId="invoice-copy-Acct"
                            initialValue={copyInvoice.baseInfoDetail.invNo ?? ''}
                            onSelect={c => {
                                const tempbaseInfo = copyInvoice.baseInfoDetail;
                                tempbaseInfo.invNo = c.invNo;
                                tempbaseInfo.refNo = c.invNo;
                                tempbaseInfo.jobId = c.id;
                                onChange({ baseInfoDetail: tempbaseInfo })
                            }}
                            onTextChange={c => {
                                const tempbaseInfo = copyInvoice.baseInfoDetail;
                                tempbaseInfo.invNo = c ?? '';
                                tempbaseInfo.scrJobId = 0;
                                tempbaseInfo.jobId = 0;
                                onChange({ baseInfoDetail: tempbaseInfo })
                            }}
                            placeholder={undefined}
                            jobType={jobName}
                            parent={srcJobName}
                        />
                    </InlineInputControlRow>}
                </div>
                {!isFromCopy &&
                    <div style={{ paddingBottom: '10px', width: '370px' }}>
                        <InlineInputControlRow smallMargin mode="w150" label={copyInvoice?.fieldLabel.mblName} alignRight={true}>
                            <TextBox noborder disabled value={copyInvoice.baseInfoDetail.mblNo ?? ''} />
                        </InlineInputControlRow>
                    </div>
                }
                {!isFromCopy && copyInvoice.fieldLabel.hblName && copyInvoice.fieldLabel.hblName.length > 0 &&
                    <div style={{ paddingBottom: '10px', width: '370px' }}>
                        <InlineInputControlRow smallMargin mode="w150" label={copyInvoice.fieldLabel.hblName} alignRight={true}>
                            <TextBox noborder disabled value={copyInvoice.baseInfoDetail.hblNo ?? ''} />
                        </InlineInputControlRow>
                    </div>
                }
            </div>
            <div className='report-footer'>
                <PrimaryButton id="copyfrom-ok-button" focusable size="regular" style={{ width: '90px' }} onClick={onCopy} >OK</PrimaryButton>
                <PrimaryButton id="copyfrom-close-button" focusable size="regular" style={{ width: '90px' }} onClick={onPopupClose} onTabNext={(e:React.KeyboardEvent) => {
                    // Tab이동이 OK, Cancel 버튼 사이에서만 동작해서 강제로 설정한 것임. 나중에 수정 필요.  useJobMenu.tsx의 onKeyDown 함수에서 
                    // if ((reactModal && !reactModal.contains(currentNode)) ... 조건의 ! 를 제거하면 동작하는데 다른 영향이 있어서 그쪽 개선하면 이 로직 제거하면 됨.  
                    // 명수씨가 작업해야 함.  2026-08-20 chuck
                    e.stopPropagation();
                    e.preventDefault();
                    setTimeout(() => {
                        const lookupId = (isFromCopy ? 'invoice-copy-Acct' : 'invoice-copy');
                        document.getElementById(lookupId)?.focus();
                    }, 30);
                }} >CLOSE</PrimaryButton>
             </div>
        </div>
    );

}

const CopyInvoice = ({
  onPopupClose,
  jobId,
  jobName,
  srcJobName,
  srcJobId,
  isFromCopy,
  invoiceNo,
}: {
  onPopupClose: () => void,
  jobId: number,
  jobName: JobNames,
  srcJobId: number,
  srcJobName: JobNames,
  isFromCopy?: boolean,
  invoiceNo?: string,
}) => {
  return (
    <CopyInvoiceImpl
      jobName={jobName}
      jobId={jobId}
      srcJobName={srcJobName}
      srcJobId={srcJobId}
      onPopupClose={onPopupClose}
      key={`CopyInvoice-details`}
      isFromCopy = {isFromCopy}
      invoiceNo = {invoiceNo}
    />
  );
};

export default CopyInvoice;